import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { translate, translateObject } from '@jsverse/transloco';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { SimpleDialogButtonColor } from 'app/components/simple-dialog.component';
import { configRouteNameValidatorFactory } from 'app/modules/configuration/configuration-utils';
import { Session } from 'app/providers/bridge-api/auth.service';
import {
  ConfigurationRoute,
  ConfigurationRouteService,
} from 'app/providers/semantria-api/configuration-route.service';
import { ConfigurationService } from 'app/providers/semantria-api/configuration.service';
import { AlertService } from 'app/services/alert.service';
import { DialogService } from 'app/services/dialog.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';

import { ConfigurationRouteListService } from '../../services/configuration-route-list.service';

@EnableClassTelemetry()
@Component({
  selector: 'app-configuration-route-edit',
  templateUrl: './configuration-route-edit.component.html',
  styleUrls: ['./configuration-route-edit.component.scss'],
  standalone: false,
})
export class ConfigurationRouteEditComponent implements OnChanges {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() configurationRoute: ConfigurationRoute;
  @Input() hasAutoAnalysis;
  @Input() canEditRoute;

  configurationRouteDto: ConfigurationRoute;

  session: Session;

  routes: ConfigurationRoute[];
  nameValidator;
  hasOngoingAnalysis;
  isNameValid = true;
  isCallbackValid = true;

  isSaving: boolean;

  unsub$ = new Subject();

  constructor(
    private configRouteService: ConfigurationRouteService,
    private configService: ConfigurationService,
    private alertService: AlertService,
    private dialogService: DialogService,
    private listService: ConfigurationRouteListService
  ) {
    this.listService.onRoutesLoaded.pipe(takeUntil(this.unsub$)).subscribe((routes) => {
      this.routes = routes;
      this.nameValidator = configRouteNameValidatorFactory(this.routes, this.configurationRoute);
    });
    this.listService.onBeforeRouteSelected.pipe(takeUntil(this.unsub$)).subscribe((route) => {
      if (route.route_id !== this.configurationRoute.route_id) {
        this.checkChanges((proceed) => {
          if (proceed) {
            this.listService.afterRouteSelected(route);
          }
        });
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.configurationRoute?.currentValue) {
      this.configurationRouteDto = JSON.parse(JSON.stringify(this.configurationRoute));
      this.nameValidator = configRouteNameValidatorFactory(this.routes, this.configurationRoute);
    }
  }

  cancelEdit() {
    this.configurationRouteDto = JSON.parse(JSON.stringify(this.configurationRoute));
  }

  async saveEdit() {
    this.isSaving = true;

    try {
      await this.configRouteService.updateConfigurationRoute(this.configurationRouteDto);
      this.configService.clearCache();

      this.configurationRoute.name = this.configurationRouteDto.name;
      this.configurationRoute.notes = this.configurationRouteDto.notes;
      this.configurationRoute.response_type = this.configurationRouteDto.response_type;
      this.configurationRoute.callback_url = this.configurationRouteDto.callback_url;

      this.listService.routeUpdated(this.configurationRoute);

      this.alertService.showToast(
        translate('configuration-routes.view.settings-tab.edit-success', {
          route: this.configurationRoute.name,
        })
      );
    } catch (err) {
      this.alertService.showErrorToast(
        translate('configuration-routes.view.settings-tab.edit-failed', {
          route: this.configurationRoute.name,
        }),
        err
      );
    } finally {
      this.isSaving = false;
    }
  }

  hasConfigurations(route) {
    return !!Object.keys(route.configuration_map).length;
  }

  async checkChanges(resolve) {
    if (!this.hasChanges) {
      resolve(true);
      return;
    }

    const translations = translateObject('configuration-routes.view.settings-tab.unsaved-changes');

    let content = translations['content-invalid'];
    let confirmTitle;
    if (this.isNameValid && this.isCallbackValid) {
      content = translations['content-valid'];
      confirmTitle = translations['confirm'];
    }

    this.dialogService
      .showSimpleDialog({
        title: translations['title'],
        content,
        confirmTitle,
        confirmColor: SimpleDialogButtonColor.green,
        cancelTitle: translations['cancel'],
        buttons: [
          {
            title: translations['discard'],
            color: SimpleDialogButtonColor.orange,
            handler: (dialogRef) => {
              dialogRef.close();
              this.cancelEdit();

              resolve(true);
            },
          },
        ],
      })
      .afterClosed()
      .subscribe(async (result) => {
        if (result) {
          await this.saveEdit();

          resolve(true);
        } else {
          resolve(false);
        }
      });
  }

  get hasChanges() {
    if (this.configurationRouteDto) {
      return (
        /* eslint-disable eqeqeq */
        this.configurationRouteDto.name != this.configurationRoute.name ||
        this.configurationRouteDto.notes != this.configurationRoute.notes ||
        this.configurationRouteDto.response_type != this.configurationRoute.response_type ||
        this.configurationRouteDto.callback_url != this.configurationRoute.callback_url
        /* eslint-enable eqeqeq */
      );
    }
  }
}
